import dayjs from 'dayjs';

import { Carousel } from '@mantine/carousel';
import { Flex, Grid, Skeleton } from '@mantine/core';

import { useQuery } from '@tanstack/react-query';

import { selectActiveBookings } from '@/store/app-process/app-selectors.ts';
import { selectPasses } from '@/store/user-process/user-selectors.ts';

import { BookingCard } from '@/entities/BookingCard/BookingCard';

import { bookingObjectsApi } from '@/shared/api/bookingObjects.ts';
import { BOOKING_TYPES, TODAY } from '@/shared/constant/app';
import { resetTime } from '@/shared/helpers/utils.ts';
import useAppStore from '@/shared/store/app.store.ts';
import { useSettingsStore } from '@/shared/store/settings.store.ts';

import { useAppSelector } from '@/hooks';

export const BookingCards = () => {
	const date = useAppStore((state) => state.date);
	const location = useSettingsStore((state) => state.location);
	const currentLocation = useSettingsStore((state) => state.currentLocation);
	const passes = useAppSelector(selectPasses);
	const activeBookings = useAppSelector(selectActiveBookings);

	const isValidDate = Boolean(
		date?.[0] && date?.[1] && dayjs(date[0]).isValid() && dayjs(date[1]).isValid(),
	);

	const { data: objectLoad, isLoading: isLoadingObjectLoad } = useQuery({
		queryKey: ['objectLoad', currentLocation, date],
		queryFn: () => currentLocation && bookingObjectsApi.getObjectLoad(currentLocation?.id, date),
		enabled: isValidDate,
	});

	const { data: parkingLoad, isLoading: isLoadingParkingObjectLoad } = useQuery({
		queryKey: ['parkingLoad', location, date],
		queryFn: () => location && bookingObjectsApi.getObjectLoad(location.parking[0].id, date),
		enabled: isValidDate && !objectLoad?.parking_place,
	});

	return (
		<Grid gutter={8} grow>
			{BOOKING_TYPES?.map((item) => {
				if (
					(item === 'parking_place' && !passes?.find((pass) => pass.pass_type.kind === 'car')) ||
					(item === 'parking_place' &&
						resetTime(dayjs(date?.[1])).diff(resetTime(TODAY), 'day') > 14)
				)
					return;

				const activeBooking = activeBookings?.find(
					(booking) => booking.booking_object.type.kind === item,
				);

				return (
					<Grid.Col span={{ base: 6, xs: 6, sm: 6, md: 6, lg: 3 }} key={item}>
						<Skeleton radius={16} visible={isLoadingObjectLoad || isLoadingParkingObjectLoad}>
							{activeBooking ? (
								<Flex h={100}>
									<Carousel
										flex={1}
										orientation="vertical"
										align="start"
										height="100%"
										withControls={false}
										withIndicators={!!activeBooking}
										slideGap={10}
										slidesToScroll={1}
										styles={{
											indicators: {
												bottom: 42,
												right: 6,
											},
											indicator: {
												width: 6,
												height: 6,
												backgroundColor: '#ffffff',
												border: '0.5px solid gray',
											},
										}}
										loop
									>
										{activeBooking && dayjs(date[0]).isToday() && (
											<Carousel.Slide>
												<BookingCard
													type={item}
													data={{
														load: {
															...objectLoad,
															...(!objectLoad?.parking_place ? { ...parkingLoad } : {}),
														},
													}}
													place={activeBooking.booking_object.name.replace(/\s\|\s[\d\D]+/gm, '')}
												/>
											</Carousel.Slide>
										)}
										<Carousel.Slide>
											<BookingCard
												type={item}
												data={{
													load: {
														...objectLoad,
														...(!objectLoad?.parking_place ? { ...parkingLoad } : {}),
													},
												}}
											/>
										</Carousel.Slide>
									</Carousel>
								</Flex>
							) : (
								<BookingCard
									type={item}
									data={{
										load: {
											...objectLoad,
											...(!objectLoad?.parking_place ? { ...parkingLoad } : {}),
										},
									}}
								/>
							)}
						</Skeleton>
					</Grid.Col>
				);
			})}
		</Grid>
	);
};
